import { useEffect } from 'react';
import {
  Button,
  InfoDataTable,
  ModalComponentWrapper,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { formatReceivedAmount, useModalStore } from '@/shared/lib';
import {
  EXCHANGE_RESULT_VARIANTS,
  useExchangeResult,
  type ExchangeConfirmationArgs,
} from '../model';
import { useAssetsExchange } from '../api';
import { useAfterOperationRefresh } from '@/entities/operation';
import { isNetworkError } from '../lib';

const ExchangeConfirmationModal = ({
  title,
  amountToPay,
  amountToReceive,
  assetCodeToPay,
  assetCodeToReceive,
  conversionRate,
  formData,
  resultVariant = EXCHANGE_RESULT_VARIANTS.PAGE,
  onSuccess,
}: ExchangeConfirmationArgs) => {
  const { t } = useTranslation();
  const { closeDialog, setCloseLocked } = useModalStore();

  const afterOperationRefresh = useAfterOperationRefresh();
  const { goToSuccessScreen, goToErrorScreen, goToNetworkErrorScreen } =
    useExchangeResult(resultVariant);

  const { exchangeAssets, isExchangeAssetsLoading } = useAssetsExchange({
    onSuccess: () => {
      onSuccess?.();
      goToSuccessScreen();
      afterOperationRefresh();
    },
    onError: error => {
      if (isNetworkError(error)) {
        goToNetworkErrorScreen(afterOperationRefresh);
        return;
      }

      goToErrorScreen();
    },
  });

  useEffect(() => {
    setCloseLocked(isExchangeAssetsLoading);

    return () => setCloseLocked(false);
  }, [isExchangeAssetsLoading, setCloseLocked]);

  const data = [
    {
      key: t('exchange.sectionSell'),
      value: `${formatReceivedAmount(amountToPay)} ${assetCodeToPay}`,
    },
    {
      key: t('exchange.sectionBuy'),
      value: `≈ ${formatReceivedAmount(amountToReceive)} ${assetCodeToReceive}`,
    },
    {
      key: t('exchange.exchangeRate'),
      value: `${conversionRate.leftPart} ≈ ${conversionRate.rightPart}`,
    },
  ];

  const handleSubmit = async () => {
    setCloseLocked(true);
    await exchangeAssets(formData);
  };

  return (
    <ModalComponentWrapper title={title}>
      <InfoDataTable
        data={data}
        className="py-[1.6rem] px-[2.4rem] bg-secondary-bg rounded-[1.6rem]"
      />
      <div className="w-full flex gap-[1.6rem]">
        <Button
          variant="secondary"
          size="lg"
          disabled={isExchangeAssetsLoading}
          onClick={closeDialog}
          className="flex-1">
          {t('common.cancel')}
        </Button>
        <Button
          variant="default"
          loading={isExchangeAssetsLoading}
          size="lg"
          onClick={handleSubmit}
          className="flex-1">
          {t('common.submit')}
        </Button>
      </div>
      <p className="text-center p4 text-secondary">
        {t('exchange.confirmationDescription')}
      </p>
    </ModalComponentWrapper>
  );
};

export { ExchangeConfirmationModal };
